import { prepareLocalSessionClose } from '../account/localSession'
import { hasLocalSaveFailure } from '../pages/localSaveState'
import { computed, readonly, ref } from 'vue'
import { check, type DownloadEvent, type Update } from '@tauri-apps/plugin-updater'
import { exit, relaunch } from '@tauri-apps/plugin-process'
import { getCurrentWindow } from '@tauri-apps/api/window'
import { IS_MOBILE_OS } from '../../utils/appVariant'

export type UpdateMode = 'automatic' | 'ask' | 'manual'
export type UpdateStatus =
  | 'idle'
  | 'checking'
  | 'up-to-date'
  | 'available'
  | 'downloading'
  | 'ready'
  | 'installing'
  | 'error'

const MODE_STORAGE_KEY = 'ink-device-update-mode-v1'
const LAST_CHECKED_STORAGE_KEY = 'ink-device-update-last-checked-v1'
const DAILY_CHECK_INTERVAL = 24 * 60 * 60 * 1000
const STARTUP_CHECK_DELAY = 2500

function loadMode(): UpdateMode {
  try {
    const value = localStorage.getItem(MODE_STORAGE_KEY)
    if (value === 'automatic' || value === 'ask' || value === 'manual') return value
  } catch {}
  return 'automatic'
}

function loadLastChecked(): Date | null {
  try {
    const value = localStorage.getItem(LAST_CHECKED_STORAGE_KEY)
    if (!value) return null
    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  } catch {
    return null
  }
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

// Phones and tablets update through their app stores, not this updater.
const supported = !__IS_WEB__ && !IS_MOBILE_OS
const mode = ref<UpdateMode>(loadMode())
const status = ref<UpdateStatus>('idle')
const availableVersion = ref<string | null>(null)
const releaseNotes = ref<string | null>(null)
const releaseDate = ref<string | null>(null)
const lastChecked = ref<Date | null>(loadLastChecked())
const downloadedBytes = ref(0)
const totalBytes = ref<number | null>(null)
const installWhenClosed = ref(false)
const error = ref<string | null>(null)
const noticeDismissed = ref(false)

let availableUpdate: Update | null = null
let started = false
let prepareForUpdate: (() => void | Promise<void>) | null = null

const downloadProgress = computed(() => {
  if (!totalBytes.value || totalBytes.value <= 0) return null
  return Math.min(100, Math.round((downloadedBytes.value / totalBytes.value) * 100))
})

const noticeVisible = computed(() => {
  if (noticeDismissed.value) return false
  return status.value === 'available' || status.value === 'downloading' || status.value === 'ready' || status.value === 'installing'
})

function recordSuccessfulCheck() {
  const checked = new Date()
  lastChecked.value = checked
  try {
    localStorage.setItem(LAST_CHECKED_STORAGE_KEY, checked.toISOString())
  } catch {}
}

function applyUpdateMetadata(update: Update) {
  availableVersion.value = update.version
  releaseNotes.value = update.body?.trim() || null
  releaseDate.value = update.date || null
}

async function checkForUpdates(): Promise<void> {
  if (
    !supported
    || status.value === 'checking'
    || status.value === 'available'
    || status.value === 'downloading'
    || status.value === 'ready'
    || status.value === 'installing'
  ) return

  status.value = 'checking'
  error.value = null

  try {
    const update = await check()
    recordSuccessfulCheck()

    if (!update) {
      availableUpdate = null
      availableVersion.value = null
      releaseNotes.value = null
      releaseDate.value = null
      installWhenClosed.value = false
      status.value = 'up-to-date'
      return
    }

    availableUpdate = update
    applyUpdateMetadata(update)
    noticeDismissed.value = false
    status.value = 'available'

    if (mode.value === 'automatic') await downloadAvailableUpdate()
  } catch (cause) {
    error.value = errorMessage(cause)
    status.value = 'error'
  }
}

function handleDownloadEvent(event: DownloadEvent) {
  if (event.event === 'Started') {
    downloadedBytes.value = 0
    totalBytes.value = event.data.contentLength ?? null
  } else if (event.event === 'Progress') {
    downloadedBytes.value += event.data.chunkLength
  } else if (event.event === 'Finished' && totalBytes.value) {
    downloadedBytes.value = totalBytes.value
  }
}

async function downloadAvailableUpdate(): Promise<void> {
  if (!supported || !availableUpdate || status.value === 'downloading' || status.value === 'installing') return

  status.value = 'downloading'
  error.value = null
  noticeDismissed.value = false
  downloadedBytes.value = 0
  totalBytes.value = null

  try {
    await availableUpdate.download(handleDownloadEvent)
    status.value = 'ready'
  } catch (cause) {
    error.value = errorMessage(cause)
    status.value = 'error'
  }
}

async function installDownloadedUpdate(restartAfterInstall: boolean): Promise<void> {
  if (!availableUpdate || status.value !== 'ready') return

  status.value = 'installing'
  error.value = null
  noticeDismissed.value = false

  let resume = () => {}
  let installed = false
  try {
    resume = await prepareLocalSessionClose()
    await prepareForUpdate?.()
    if (hasLocalSaveFailure.value) throw new Error('Local writing must be saved before updating')
    await availableUpdate.install({ restartAfterInstall })

    // Windows exits from install(). macOS and Linux return after replacing the
    // app, so finish the requested lifecycle explicitly on those platforms.
    if (restartAfterInstall) await relaunch()
    else await exit(0)
    installed = true
  } catch (cause) {
    error.value = errorMessage(cause)
    status.value = 'error'
  } finally { if (!installed) resume() }
}

async function restartAndInstall(): Promise<void> {
  installWhenClosed.value = false
  await installDownloadedUpdate(true)
}

function toggleInstallWhenClosed(): void {
  if (status.value !== 'ready') return
  installWhenClosed.value = !installWhenClosed.value
  noticeDismissed.value = false
}

function dismissUpdateNotice(): void {
  if (status.value !== 'installing') noticeDismissed.value = true
}

function setUpdateMode(nextMode: UpdateMode): void {
  if (mode.value === nextMode) return
  mode.value = nextMode
  try {
    localStorage.setItem(MODE_STORAGE_KEY, nextMode)
  } catch {}

  if (!supported || nextMode === 'manual') return
  if (nextMode === 'automatic' && status.value === 'available') {
    void downloadAvailableUpdate()
  } else if (status.value === 'idle' || status.value === 'error') {
    void checkForUpdates()
  }
}

export function setUpdatePreparationHandler(handler: (() => void | Promise<void>) | null): void {
  prepareForUpdate = handler
}

async function installOnWindowClose(event: { preventDefault: () => void }) {
  if (!installWhenClosed.value || status.value !== 'ready') return
  event.preventDefault()
  await installDownloadedUpdate(false)
}

export function startUpdateManager(): void {
  if (!supported || started) return
  started = true

  void getCurrentWindow().onCloseRequested(installOnWindowClose).catch((cause) => {
    console.warn('[Updater] Could not register install-on-close:', cause)
  })

  if (mode.value !== 'manual') {
    window.setTimeout(() => {
      if (mode.value !== 'manual') void checkForUpdates()
    }, STARTUP_CHECK_DELAY)
  }

  window.setInterval(() => {
    if (mode.value !== 'manual') void checkForUpdates()
  }, DAILY_CHECK_INTERVAL)
}

export const updater = {
  supported,
  mode: readonly(mode),
  status: readonly(status),
  availableVersion: readonly(availableVersion),
  releaseNotes: readonly(releaseNotes),
  releaseDate: readonly(releaseDate),
  lastChecked: readonly(lastChecked),
  downloadedBytes: readonly(downloadedBytes),
  totalBytes: readonly(totalBytes),
  downloadProgress,
  installWhenClosed: readonly(installWhenClosed),
  error: readonly(error),
  noticeVisible,
  checkForUpdates,
  downloadAvailableUpdate,
  restartAndInstall,
  toggleInstallWhenClosed,
  dismissUpdateNotice,
  setUpdateMode,
}
